import { FC, useState } from 'react';
import { ReportViewProps } from './ReportView.types';
import { ProductionVolumeSection } from './components/ProductionVolumeSection';
import { AvailabilityMetrics } from './components/AvailabilityMetrics';
import { TabChips } from './components/TabChips';
import { Container, Header, TitleContainer, ContentGrid } from './ReportView.styles';
import { AllieText } from '../../../components/Text';
import { AllieChip } from '../../../components';
import { Conclusion } from './components/Conclusion';
import { GeneralIndicators } from '../../components/GeneralIndicators';
import { GeneralCostMetrics } from './components/GeneralCostMetrics';
import { ProductCostSection } from './components/ProductCostCard';
import { ReportEmptyState } from './components/ReportEmptyState/ReportEmptyState';
import { secondsToHoursMinutes } from '../../../common/utils';

// Legacy
const MACHINE_UNASSIGNED_NAME = 'Sin asignar';

export const ReportView: FC<ReportViewProps> = ({
  dateRange,
  stations,
  labels,
  machineUnassignedName,
  onStationSelected,
}) => {
  const [selectedStation, setSelectedStation] = useState(stations[0]?.name || '');

  const tabs = stations.map((station) => ({
    value: station.name,
    label: station.name,
  }));

  const handleStationSelected = (station: string) => {
    setSelectedStation(station);
    onStationSelected(station);
  };

  const selectedStationData = stations.find((station) => station.name === selectedStation);

  if (!selectedStationData) return null;

  const { kpi, production, availability, stops, conclusion, cost, costByProduct, isEmpty } =
    selectedStationData;

  const toPercentage = (value: number) => Math.round(value * 100);

  const mostStoppedMachine = (() => {
    if (!stops?.byMachine || stops.byMachine.length === 0) return undefined;

    const sortedMachines = [...stops.byMachine].sort(
      (a, b) => b.downtimeSecs - a.downtimeSecs
    );

    const machineNotUnassigned = sortedMachines.find(
      (machine) => 
        machine.name !== '' && 
        machine.name !== MACHINE_UNASSIGNED_NAME &&
        machine.name !== machineUnassignedName
    );

    if (!machineNotUnassigned && sortedMachines[0]) {
      return {
        ...sortedMachines[0],
        name: machineUnassignedName || MACHINE_UNASSIGNED_NAME,
      };
    }

    return machineNotUnassigned;
  })();

  return (
    <Container data-testid="reports-preview">
      <Header>
        <TitleContainer>
          <AllieText type="body1" weight={500}>
            {labels.title}
          </AllieText>
          <AllieChip
            color={{
              backgroundColor: 'neutral-5',
              allieColor: 'primary-90',
              borderColor: 'neutral-30',
            }}
            radius="full"
          >
            {dateRange}
          </AllieChip>
        </TitleContainer>
        <TabChips tabs={tabs} selectedTab={selectedStation} onTabChange={handleStationSelected} />
      </Header>

      {isEmpty ? (
        <ReportEmptyState 
          title={labels.emptyState.title}
          description={labels.emptyState.description
            .replace('{name}', selectedStation)
            .replace('{date}', dateRange)
          }
        />
      ) : (
        <ContentGrid>
          {kpi && (
            <GeneralIndicators
              oee={toPercentage(kpi.oee)}
              availability={toPercentage(kpi.availability)}
              performance={toPercentage(kpi.performance)}
              quality={toPercentage(kpi.quality)}
              labels={labels.indicators}
              breakpoint="530px"
            />
          )}

          {cost && <GeneralCostMetrics data={cost} labels={labels.generalCost} />}

          {production && (
            <ProductionVolumeSection
              weeklyProduction={production.total}
              estimatedProduction={production.planned}
              unit={production.unit}
              labels={labels.production}
            />
          )}

          {availability && mostStoppedMachine && (
            <AvailabilityMetrics
              downtime={secondsToHoursMinutes(availability.unplannedStopTimeInSeconds)}
              availableTime={secondsToHoursMinutes(availability.productiveTimeInSeconds)}
              mostStoppedMachine={
                mostStoppedMachine
                  ? {
                      name: mostStoppedMachine?.name,
                      hours: secondsToHoursMinutes(mostStoppedMachine?.downtimeSecs || 0),
                    }
                  : undefined
              }
              labels={labels.availability}
            />
          )}

          {costByProduct && costByProduct.length > 0 && (
            <ProductCostSection
              costByProduct={costByProduct}
              labels={labels.productCost}
            />
          )}

          {conclusion && (
            <Conclusion
              conclusion={conclusion}
              labels={labels.conclusion}
              name={selectedStation}
              date={dateRange}
            />
          )}
        </ContentGrid>
      )}
    </Container>
  );
};
